import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_ImageGallery = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🖼️ CSS Image Gallery</h1>

      <p className="tutorial-intro">
        An <b>image gallery</b> is a collection of images displayed in a grid or
        flexible layout. CSS allows us to build galleries that are responsive,
        attractive, and interactive without much code.
      </p>

      {/* Why Learn Image Gallery */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#1e3a8a", marginBottom: "0.5rem" }}>
          ✨ Why Learn CSS Image Gallery?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Image galleries are used in portfolios, product showcases, and
          websites of all kinds. With CSS, you can create responsive grids,
          hover effects, and lightbox-style galleries.
        </p>
      </div>

      {/* Key Topics */}
      <div className="tutorial-section">
        <h2 className="section-title">📘 Image Gallery Topics</h2>
        <ul className="tutorial-list">
          <li>
            <a href="/css/imagegallery/grid" className="learn-more">
              CSS Grid Gallery
            </a>{" "}
            – Build galleries using modern CSS Grid layout.
          </li>
          <li>
            <a href="/css/imagegallery/flexbox" className="learn-more">
              CSS Flexbox Gallery
            </a>{" "}
            – Use Flexbox for responsive image layouts.
          </li>
          <li>
            <a href="/css/imagegallery/hover" className="learn-more">
              Hover Effects
            </a>{" "}
            – Add zoom, fade, or overlay effects on hover.
          </li>
          <li>
            <a href="/css/imagegallery/responsive" className="learn-more">
              Responsive Gallery
            </a>{" "}
            – Make galleries look good on all devices.
          </li>
          <li>
            <a href="/css/imagegallery/lightbox" className="learn-more">
              CSS + Lightbox
            </a>{" "}
            – Open images in a fullscreen preview.
          </li>
        </ul>
      </div>

      {/* Example 1: Grid Gallery */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Simple Grid Gallery</h2>
        <p>
          A <b>CSS Grid</b> gallery that automatically adjusts the number of
          columns based on screen size.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}

.gallery-grid img {
  width: 100%;
  border-radius: 8px;
  transition: transform 0.3s;
}

.gallery-grid img:hover {
  transform: scale(1.05);
}`}
          language="css"
        />

        <div
          className="gallery-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "10px",
            width: "100%",
            marginTop: "1rem",
          }}
        >
          <img
            src="https://picsum.photos/200?1"
            style={{ width: "100%" }}
            alt="sample1"
          />
          <img
            src="https://picsum.photos/200?2"
            style={{ width: "100%" }}
            alt="sample2"
          />
          <img
            src="https://picsum.photos/200?3"
            style={{ width: "100%" }}
            alt="sample3"
          />
          <img
            src="https://picsum.photos/200?4"
            style={{ width: "100%" }}
            alt="sample4"
          />
          <img
            src="https://picsum.photos/200?5"
            style={{ width: "100%" }}
            alt="sample5"
          />
          <img
            src="https://picsum.photos/200?6"
            style={{ width: "100%" }}
            alt="sample6"
          />
        </div>
      </div>

      {/* Example 2: Flexbox Gallery */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: Flexbox Gallery</h2>
        <p>
          A <b>Flexbox</b> gallery where images wrap into new rows and adjust
          their width.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
.gallery-flex {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.gallery-flex img {
  width: 200px;
  border-radius: 8px;
  transition: transform 0.3s;
}

.gallery-flex img:hover {
  transform: scale(1.05);
}`}
          language="css"
        />

        <div
          className="gallery-flex"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: "center",
            marginTop: "1rem",
          }}
        >
          <img src="https://picsum.photos/200?7" alt="sample7" />
          <img src="https://picsum.photos/200?8" alt="sample8" />
          <img src="https://picsum.photos/200?9" alt="sample9" />
          <img src="https://picsum.photos/200?10" alt="sample10" />
          <img src="https://picsum.photos/200?11" alt="sample11" />
        </div>
      </div>

      {/* Example 3: Hover Overlay */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Hover Overlay Effect</h2>
        <p>
          Add an overlay with text when hovering over an image in the gallery.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
.gallery-overlay {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}

.gallery-overlay .item {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
}

.gallery-overlay img {
  width: 100%;
  display: block;
}

.gallery-overlay .overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 10px;
  text-align: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.gallery-overlay .item:hover .overlay {
  opacity: 1;
}`}
          language="css"
        />

        <div
          className="gallery-overlay"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "10px",
            marginTop: "1rem",
          }}
        >
          {[12, 13, 14, 15].map((id) => (
            <div key={id} className="item" style={{ position: "relative" }}>
              <img
                src={`https://picsum.photos/300?${id}`}
                alt={`sample${id}`}
                style={{ width: "100%" }}
              />
              <div
                className="overlay"
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "rgba(0,0,0,0.6)",
                  color: "white",
                  padding: "10px",
                  textAlign: "center",
                  opacity: 0,
                  transition: "opacity 0.3s",
                }}
              >
                Image {id}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Example 4: Card Gallery */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 4: Card-style Gallery</h2>
        <p>
          A <b>card gallery</b> displays each image inside a styled card with
          captions or descriptions.
        </p>
        <CodeCopyBlock
          code={`.gallery-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 15px;
}

.gallery-cards .card {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  background: white;
}

.gallery-cards img {
  width: 100%;
  display: block;
}

.gallery-cards .card-content {
  padding: 10px;
  text-align: center;
}`}
          language="css"
        />
        <div
          className="gallery-cards"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "15px",
            marginTop: "1rem",
          }}
        >
          {[41, 42, 43].map((id) => (
            <div
              className="card"
              key={id}
              style={{
                border: "1px solid #ddd",
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                padding: "0",
                background: "white",
              }}
            >
              <img src={`https://picsum.photos/300?${id}`} alt={`card${id}`} />
              <div
                className="card-content"
                style={{ padding: "10px", textAlign: "center" }}
              >
                <h4>Image {id}</h4>
                <p>This is a sample description.</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know how to build <b>grid</b>, <b>flexbox</b>, and{" "}
          <b>hover-overlay</b> galleries, try combining them with{" "}
          <a href="/css/imagegallery/responsive" className="learn-more">
            responsive design
          </a>{" "}
          and even a{" "}
          <a href="/css/imagegallery/lightbox" className="learn-more">
            lightbox
          </a>{" "}
          for a professional image showcase.
        </p>
      </div>
    </div>
  );
};

export default CSS_ImageGallery;
